"use client";

import { useState, type CSSProperties } from "react";
import { Check, GripVertical, RotateCcw, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    CardSortingBlock as CardSortingBlockType,
    LomdaTheme,
    SortCard,
    SortCategory,
} from "@/lib/utils/lomdaV3/types";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import { useResolvedGridGap } from "../../../hooks/useGridGap";
import { DEFAULT_THEME } from "../../../lib/theme";
import { CARD_SORTING_DEFAULTS } from "../../toolbar/cardSortingSettings";
import RichText from "../text/richText";
import { stripHtml, wrapInTag } from "../../../lib/titleHtml";
import { fieldTag } from "../../../lib/sectionRegistry";
import { PreviewButton } from "../shared/PreviewButton";
import RichHtml from "./richHtml";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";

const POOL = "__pool";

/** Resolved cards-grid styling (with defaults applied). */
interface CardStyle {
    columns: number;
    gap: number;
    cardStyle: CSSProperties;
}

function resolveStyle(
    data: CardSortingBlockType["data"],
    theme: LomdaTheme
): CardStyle {
    const background = data.cardBackground ?? theme.card;
    const borderWidth =
        data.cardBorderWidth ?? CARD_SORTING_DEFAULTS.borderWidth;
    const borderColor = data.cardBorderColor ?? theme.border;
    return {
        columns: data.columns ?? CARD_SORTING_DEFAULTS.columns,
        gap: data.gap ?? CARD_SORTING_DEFAULTS.gap,
        cardStyle: {
            backgroundColor: background,
            borderStyle: "solid",
            borderWidth: `${borderWidth}px`,
            borderColor,
        },
    };
}

function PreviewSorting({
    categories,
    cards,
    style,
}: {
    categories: SortCategory[];
    cards: SortCard[];
    style: CardStyle;
}) {
    const locale = useLomdaLocale();
    const isMobile = useIsMobile();
    // cardId -> categoryId, or absent/POOL = still in the pool.
    const [assign, setAssign] = useState<Record<string, string>>({});
    const [checked, setChecked] = useState(false);
    const [dragId, setDragId] = useState<string | null>(null);
    const [over, setOver] = useState<string | null>(null);

    const placeOf = (id: string) => assign[id] ?? POOL;
    const pool = cards.filter((c) => placeOf(c.id) === POOL);
    const inCategory = (catId: string) =>
        cards.filter((c) => placeOf(c.id) === catId);

    const allAssigned = cards.every((c) => placeOf(c.id) !== POOL);
    const correctCount = cards.filter(
        (c) => assign[c.id] === c.categoryId
    ).length;

    const drop = (target: string) => {
        if (!dragId || checked) return;
        setAssign((a) => ({ ...a, [dragId]: target }));
        setDragId(null);
        setOver(null);
    };

    const dropProps = (target: string) => ({
        onDragOver: (e: React.DragEvent) => {
            if (!dragId || checked) return;
            e.preventDefault();
            setOver(target);
        },
        onDragLeave: () => setOver((o) => (o === target ? null : o)),
        onDrop: () => drop(target),
    });

    const renderCard = (c: SortCard) => {
        const right = assign[c.id] === c.categoryId;
        return (
            <div
                key={c.id}
                draggable={!checked}
                onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", c.id);
                    setDragId(c.id);
                }}
                onDragEnd={() => {
                    setDragId(null);
                    setOver(null);
                }}
                style={!checked ? style.cardStyle : undefined}
                className={cn(
                    "flex items-center justify-between gap-2 rounded-xl px-4 py-3 lomda-v3-text-small shadow-sm transition",
                    !checked && "lomda-v3-card-text cursor-grab active:cursor-grabbing",
                    dragId === c.id && "opacity-40",
                    checked &&
                        (right
                            ? "border-2 border-[hsl(var(--lomda-success))]/50 bg-[hsl(var(--lomda-success))]/10"
                            : "border-2 border-destructive/50 bg-destructive/5")
                )}
            >
                <span className="flex min-w-0 items-center gap-2">
                    {checked && (
                        <span
                            className={cn(
                                "flex size-5 shrink-0 items-center justify-center rounded-full text-primary-foreground",
                                right ? "bg-[hsl(var(--lomda-success))]" : "bg-destructive"
                            )}
                        >
                            {right ? (
                                <Check className="size-3.5" />
                            ) : (
                                <X className="size-3.5" />
                            )}
                        </span>
                    )}
                    <RichHtml html={c.text} className="min-w-0" />
                </span>
                {!checked && (
                    <GripVertical className="size-4 shrink-0 text-muted-foreground/40" />
                )}
            </div>
        );
    };

    const gridStyle: CSSProperties = {
        gridTemplateColumns: `repeat(${isMobile ? 1 : style.columns}, minmax(0, 1fr))`,
        gap: `${style.gap}px`,
    };

    return (
        <div className="flex flex-col gap-8">
            <div
                {...dropProps(POOL)}
                style={gridStyle}
                className={cn(
                    "grid items-start rounded-2xl p-1",
                    over === POOL && "ring-2 ring-primary/30"
                )}
            >
                {pool.map(renderCard)}
                {pool.length === 0 && (
                    <p className="lomda-v3-text-small col-span-full py-4 text-center text-muted-foreground">
                        {t("lomda-v3-editor.card-sorting.pool-empty", locale)}
                    </p>
                )}
            </div>

            <div className="h-px bg-border" />

            <div
                className="grid grid-cols-1 sm:grid-cols-2"
                style={{ gap: `${style.gap}px` }}
            >
                {categories.map((cat) => (
                    <div
                        key={cat.id}
                        {...dropProps(cat.id)}
                        style={{ gap: `${style.gap}px` }}
                        className={cn(
                            "flex min-h-[8rem] flex-col rounded-2xl border-2 border-dashed p-4 transition",
                            over === cat.id
                                ? "border-primary bg-primary/5"
                                : "border-border"
                        )}
                    >
                        <RichHtml
                            html={cat.title}
                            className="mb-3 text-center text-primary"
                        />
                        {inCategory(cat.id).map(renderCard)}
                    </div>
                ))}
            </div>

            {checked ? (
                <div className="flex items-center justify-center gap-4">
                    <span className="lomda-v3-text-small">
                        {t("lomda-v3-editor.card-sorting.score", locale, {
                            correct: correctCount,
                            total: cards.length,
                        })}
                    </span>
                    <button
                        type="button"
                        onClick={() => {
                            setChecked(false);
                            setAssign({});
                        }}
                        className="lomda-v3-text-small flex items-center gap-1.5 rounded-full border px-4 py-2 text-muted-foreground transition hover:text-foreground"
                    >
                        <RotateCcw className="size-4" />
                        {t("lomda-v3-editor.card-sorting.retry", locale)}
                    </button>
                </div>
            ) : (
                <PreviewButton
                    type="button"
                    disabled={!allAssigned}
                    onClick={() => setChecked(true)}
                >
                    {t("lomda-v3-editor.card-sorting.submit", locale)}
                </PreviewButton>
            )}
        </div>
    );
}

export default function CardSortingBlock({
    block,
}: {
    block: CardSortingBlockType;
}) {
    const locale = useLomdaLocale();
    const categoryTag = fieldTag("card-sorting", "category");
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const theme = useLomdaV3Editor((s) => s.metadata.theme) ?? DEFAULT_THEME;

    const editable = useBlockEditable();
    const { categories, cards } = block.data;
    const gridGap = useResolvedGridGap(block.id);
    const style = { ...resolveStyle(block.data, theme), gap: gridGap };
    const [dragId, setDragId] = useState<string | null>(null);
    const [over, setOver] = useState<string | null>(null);

    const update = (patch: Partial<CardSortingBlockType["data"]>) =>
        replaceBlockData(block.id, { ...block.data, ...patch });

    if (!editable) {
        const validCats = categories.filter((c) => stripHtml(c.title));
        const validCards = cards.filter(
            (c) =>
                stripHtml(c.text) &&
                validCats.some((cat) => cat.id === c.categoryId)
        );
        if (validCats.length === 0 || validCards.length === 0) return null;
        return (
            <PreviewSorting
                categories={validCats}
                cards={validCards}
                style={style}
            />
        );
    }

    const patchCat = (id: string, title: string) =>
        update({
            categories: categories.map((c) =>
                c.id === id ? { ...c, title } : c
            ),
        });
    const patchCard = (id: string, patch: Partial<SortCard>) =>
        update({
            cards: cards.map((c) => (c.id === id ? { ...c, ...patch } : c)),
        });
    const poolCards = cards.filter(
        (c) => !categories.some((cat) => cat.id === c.categoryId)
    );
    const gridStyle: CSSProperties = {
        gridTemplateColumns: `repeat(${style.columns}, minmax(0, 1fr))`,
        gap: `${style.gap}px`,
    };

    // Dropping a card on the pool ("") or a category sets its correct category —
    // the editor mirrors the preview's drag & drop instead of a dropdown.
    const assignTo = (categoryId: string) => {
        if (dragId) patchCard(dragId, { categoryId });
        setDragId(null);
        setOver(null);
    };
    const dropProps = (target: string) => ({
        onDragOver: (e: React.DragEvent) => {
            if (!dragId) return;
            e.preventDefault();
            setOver(target);
        },
        onDragLeave: () => setOver((o) => (o === target ? null : o)),
        onDrop: () => assignTo(target === POOL ? "" : target),
    });

    // A card in edit mode: the same styled card as preview, dragged by its grip
    // so the rich-text editor stays clickable.
    const editCard = (c: SortCard) => (
        <div
            key={c.id}
            style={style.cardStyle}
            className={cn(
                "lomda-v3-card-text flex items-center gap-2 rounded-xl px-4 py-2.5 shadow-sm transition",
                dragId === c.id && "opacity-40"
            )}
        >
            <span
                draggable
                onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", c.id);
                    setDragId(c.id);
                }}
                onDragEnd={() => {
                    setDragId(null);
                    setOver(null);
                }}
                className="shrink-0 cursor-grab text-muted-foreground/40 active:cursor-grabbing"
            >
                <GripVertical className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
                <RichText
                    initialHtml={c.text}
                    editable
                    useGlobalToolbar
                    blockId={block.id}
                    groupKey={`${block.id}:sort-card`}
                    placeholder={t("lomda-v3-editor.card-sorting.card-placeholder")}
                    onCommit={(text) => patchCard(c.id, { text })}
                />
            </div>
        </div>
    );

    return (
        <div className="flex flex-col gap-6">
            <div
                {...dropProps(POOL)}
                style={gridStyle}
                className={cn(
                    "grid items-start rounded-2xl p-1 transition",
                    over === POOL && "ring-2 ring-primary/30"
                )}
            >
                {poolCards.map(editCard)}
                {poolCards.length === 0 && (
                    <p className="lomda-v3-text-small col-span-full py-4 text-center text-muted-foreground">
                        {t("lomda-v3-editor.card-sorting.pool-empty", locale)}
                    </p>
                )}
            </div>

            <div className="h-px bg-border" />

            <div className="grid" style={gridStyle}>
                {categories.map((cat) => (
                    <div
                        key={cat.id}
                        {...dropProps(cat.id)}
                        className={cn(
                            "flex min-h-[8rem] flex-col rounded-2xl border-2 border-dashed p-4 transition",
                            over === cat.id
                                ? "border-primary bg-primary/5"
                                : "border-border"
                        )}
                        style={{ gap: `${style.gap}px` }}
                    >
                        <div className="mb-1 text-center">
                            <RichText
                                initialHtml={
                                    cat.title || wrapInTag(categoryTag)
                                }
                                defaultTag={categoryTag}
                                editable
                                useGlobalToolbar
                                blockId={block.id}
                                groupKey={`${block.id}:sort-category`}
                                centerPlaceholder
                                placeholder={t(
                                    "lomda-v3-editor.card-sorting.category-placeholder"
                                )}
                                editorClassName="text-center text-primary"
                                onCommit={(title) => patchCat(cat.id, title)}
                            />
                        </div>
                        {cards
                            .filter((c) => c.categoryId === cat.id)
                            .map(editCard)}
                    </div>
                ))}
            </div>
        </div>
    );
}
